import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '../settings/ConfirmModal.js';
import { formatShortDate } from '../../lib/format-date.js';
import { historyFirstLine, type HistoryKind } from '../../lib/tool-history.js';
import { useToolHistory } from '../../hooks/useToolHistory.js';
import styles from './HistorySheet.module.css';

export interface HistoryRow { id: string; heading: string; title: string }
export interface HistoryLabels {
  title: string; close: string; loading: string; empty: string; loadError: string;
  retry: string; more?: string; delete: string; deleteTitle: string;
  deleteBody: (title: string) => string; deleteConfirm: string; cancel: string; deleteError: string;
}
interface Props {
  rows: HistoryRow[]; labels: HistoryLabels; currentId?: string | null;
  loading?: boolean; error?: boolean; hasNextPage?: boolean; nextPageLoading?: boolean;
  onNextPage?: () => void; onRetry?: () => void; onClose: () => void;
  onOpen: (id: string) => void; onDelete: (id: string) => Promise<unknown>; onDeleted: (id: string) => void;
  showRowTitle?: boolean;
  /** Keep the existing chat test ids at the adapter boundary. */
  testPrefix?: string; rowPrefix?: string;
}

export function HistorySheet({ rows, labels, currentId, loading, error, hasNextPage, nextPageLoading, onNextPage, onRetry, onClose, onOpen, onDelete, onDeleted, showRowTitle = true, testPrefix = 'history', rowPrefix = 'history-row' }: Props): JSX.Element {
  const [pending, setPending] = useState<HistoryRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const deletingRef = useRef(false);
  const stateRef = useRef({ onClose, pending });
  stateRef.current = { onClose, pending };
  const deleteTestId = (id: string) => rowPrefix === 'thread' ? `thread-delete-${id}` : `history-delete-${id}`;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { if (opener?.isConnected) opener.focus(); };
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (stateRef.current.pending && event.key !== 'Tab') return;
      if (event.key === 'Escape') { event.preventDefault(); stateRef.current.onClose(); }
      if (event.key === 'Tab') {
        const container = stateRef.current.pending ? confirmRef.current : sheetRef.current;
        const buttons = Array.from(container?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
        const first = buttons[0]; const last = buttons.at(-1);
        if (!first) event.preventDefault();
        else if (!container?.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function cancelDelete(): void {
    const id = pending?.id;
    setPending(null); setDeleteFailed(false);
    queueMicrotask(() => {
      Array.from(sheetRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []).find(button => button.dataset.testid === deleteTestId(id ?? ''))?.focus();
    });
  }
  async function confirmDelete(): Promise<void> {
    if (!pending || deletingRef.current) return;
    deletingRef.current = true; setDeleting(true); setDeleteFailed(false);
    try {
      await onDelete(pending.id);
      setPending(null); onDeleted(pending.id);
      closeRef.current?.focus();
    } catch { setDeleteFailed(true); }
    finally { deletingRef.current = false; setDeleting(false); }
  }
  return <>
    <div className={styles.overlay} data-testid={`${testPrefix}-overlay`} onMouseDown={e => { if (e.target === e.currentTarget && !pending) onClose(); }}>
      <div ref={sheetRef} className={styles.sheet} role="dialog" aria-modal={pending ? undefined : true} aria-hidden={pending ? true : undefined} aria-labelledby={`${testPrefix}-title`} data-testid={`${testPrefix}-sheet`}>
        <div className={styles.handle} aria-hidden="true" />
        <div className={styles.head}>
          <h2 id={`${testPrefix}-title`} className={styles.title}>{labels.title}</h2>
          <button ref={closeRef} type="button" className={styles.close} onClick={onClose} aria-label={labels.close} data-testid={`${testPrefix}-close`}>×</button>
        </div>
        <div className={styles.body}>
          {loading && <p className={styles.note} role="status" data-testid={`${testPrefix}-loading`}>{labels.loading}</p>}
          {error && <div className={styles.errorBox}><p className={styles.error} role="alert">{labels.loadError}</p><button type="button" className={styles.retry} onClick={onRetry} data-testid={`${testPrefix}-retry`}>{labels.retry}</button></div>}
          {!loading && !error && rows.length === 0 && <p className={styles.note}>{labels.empty}</p>}
          {rows.length > 0 && <ul className={styles.list}>{rows.map(row => <li key={row.id} className={`${styles.row} ${row.id === currentId ? styles.current : ''}`}>
            <button type="button" className={styles.open} aria-current={row.id === currentId ? 'true' : undefined} onClick={() => onOpen(row.id)} data-testid={`${rowPrefix}-${row.id}`}>
              <span className={styles.rowHead}>{row.heading}</span>{showRowTitle && <span className={styles.rowTitle}>{row.title}</span>}
            </button>
            <button type="button" className={styles.delete} aria-label={labels.delete} onClick={() => { setDeleteFailed(false); setPending(row); }} data-testid={deleteTestId(row.id)}>×</button>
          </li>)}</ul>}
          {hasNextPage && <button type="button" className={styles.retry} disabled={nextPageLoading} onClick={onNextPage}>{labels.more}</button>}
        </div>
      </div>
    </div>
    {pending && <div ref={confirmRef}><ConfirmModal variant="danger" title={labels.deleteTitle} body={labels.deleteBody(historyFirstLine(pending.title, 60))} confirmLabel={labels.deleteConfirm} cancelLabel={labels.cancel} busy={deleting} error={deleteFailed ? labels.deleteError : undefined} onConfirm={() => void confirmDelete()} onCancel={cancelDelete} /></div>}
  </>;
}

/** Tool adapter: the shared sheet itself has no knowledge of API, auth or routing. */
export function ToolHistorySheet({ kind, currentId, onClose, onOpen, onDeleted }: { kind: HistoryKind; currentId: string | null; onClose: () => void; onOpen: (id: string) => void; onDeleted: (id: string) => void }): JSX.Element {
  const history = useToolHistory(kind);
  const { t, i18n } = useTranslation(['flows', 'common']);
  const rows = history.items.map(row => {
    let label = historyFirstLine(row.label);
    if (kind === 'lucky') {
      const match = /^(love|talk|money|travel|career|launch|luck) \| (\d{4}-\d{2}-\d{2})$/.exec(row.label);
      if (match) label = `${t(`lucky.goal.${match[1]}`)} | ${new Date(`${match[2]}T12:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })}`;
    }
    return { id: row.id, heading: `${formatShortDate(row.createdAt, i18n.language)} | ${label}`, title: label };
  });
  return <HistorySheet showRowTitle={false} rows={rows} currentId={currentId}
    loading={history.isLoading} error={history.isError} onRetry={() => void history.refetch()} hasNextPage={history.hasNextPage} nextPageLoading={history.isFetchingNextPage} onNextPage={() => { if (!history.isFetching) void history.fetchNextPage(); }}
    labels={{ title: t('history.title'), close: t('history.close'), loading: t('common:states.loading'), empty: t('history.empty'), loadError: t('history.loadError'), retry: t('history.retry'), more: t('history.more'), delete: t('history.delete'), deleteTitle: t('history.deleteTitle'), deleteBody: title => t('history.deleteBody', { title }), deleteConfirm: t('history.deleteConfirm'), cancel: t('common:actions.cancel'), deleteError: t('history.deleteError') }}
    onClose={onClose} onOpen={onOpen} onDelete={history.remove.mutateAsync} onDeleted={onDeleted} />;
}
